import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Bread } from "@/components/Bread";
import { CompletionScreen } from "@/components/CompletionScreen";
import { DayLoader } from "@/components/DayLoader";
import { POS } from "@/components/POS";
import { ProgressBar } from "@/components/ProgressBar";
import { Seg } from "@/components/Seg";
import { useSettings } from "@/hooks/useSettings";
import { pad, pad3, shuffle } from "@/lib/format";
import { gradeFromCorrect, review } from "@/lib/study";
import type { DayData, Word } from "@/types/lex";

type Direction = "en-vi" | "vi-en";

export function Flashcard() {
  const { day: dayParam } = useParams<{ day: string }>();
  const day = Number(dayParam) || 1;
  return (
    <DayLoader day={day}>
      {(data) => <Body day={day} data={data} />}
    </DayLoader>
  );
}

function Body({ day, data }: { day: number; data: DayData }) {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [direction, setDirection] = useState<Direction>("en-vi");
  const [deck, setDeck] = useState<readonly Word[]>(() =>
    settings.shuffleFlashcards ? shuffle(data.words) : data.words
  );
  const [i, setI] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [score, setScore] = useState({ known: 0, unknown: 0 });
  const word = deck[i];
  const finished = i >= deck.length;

  function advance(known: boolean) {
    if (word) {
      // Feed the verdict into the schedule: it sets the next review date, the
      // ledger label, and today's activity in one step.
      review(day, word.index, gradeFromCorrect(known));
    }
    setScore((s) => (known ? { ...s, known: s.known + 1 } : { ...s, unknown: s.unknown + 1 }));
    setFlipped(false);
    setTimeout(() => setI((prev) => Math.min(prev + 1, deck.length)), 200);
  }

  // Keyboard shortcuts: SPACE to flip, ← unknown, → known.
  useEffect(() => {
    if (finished) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.code === "Space") {
        e.preventDefault();
        setFlipped((f) => !f);
      } else if (e.key === "ArrowLeft") {
        advance(false);
      } else if (e.key === "ArrowRight") {
        advance(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // `i` is included so the captured `advance` closure references the
    // current word — without it, keyboard rating would mark the wrong entry.
  }, [finished, deck.length, i]);

  function restart() {
    setDeck(settings.shuffleFlashcards ? shuffle(data.words) : data.words);
    setI(0);
    setFlipped(false);
    setScore({ known: 0, unknown: 0 });
  }

  return (
    <div className="page">
      <div className="container" style={{ paddingTop: "var(--s-5)" }}>
        <Bread
          crumbs={[
            { label: "Archive", to: "/" },
            { label: `Day ${pad(day)}`, to: `/day/${day}` },
            { label: "Flashcards" },
          ]}
        />
      </div>

      <section className="container" style={{ paddingTop: "var(--s-5)" }}>
        <div className="row-s fc-head" style={{ marginBottom: "var(--s-4)" }}>
          <div className="stack" style={{ gap: 4 }}>
            <div className="t-eyebrow">Mode M01 · Flashcards</div>
            <h1 className="t-display fc-head__title" style={{ margin: 0 }}>
              {direction === "en-vi" ? "English → Tiếng Việt" : "Tiếng Việt → English"}
            </h1>
          </div>
          <div className="row-c" style={{ gap: 12 }}>
            <Seg<Direction>
              value={direction}
              onChange={setDirection}
              options={[
                ["en-vi", "EN → VI"],
                ["vi-en", "VI → EN"],
              ]}
            />
            <button className="btn btn-sm" onClick={() => navigate(`/day/${day}`)} type="button">
              Exit
            </button>
          </div>
        </div>
        <div className="row-c" style={{ gap: "var(--s-3)" }}>
          <div style={{ flex: 1 }}>
            <ProgressBar value={i} max={deck.length} />
          </div>
          <div
            className="t-mono"
            style={{ fontSize: 12, color: "var(--text-muted)", minWidth: 120, textAlign: "right" }}
          >
            {pad(i)} / {pad(deck.length)} · {deck.length - i} left
          </div>
        </div>
      </section>

      <section className="container">
        <div className="flashcard-stage">
          {!finished && word && (
            <>
              <Card
                word={word}
                day={day}
                deckPos={i}
                deckSize={deck.length}
                direction={direction}
                flipped={flipped}
                onFlip={() => setFlipped((f) => !f)}
              />

              <div className="fc-controls">
                <button
                  className="btn"
                  onClick={() => advance(false)}
                  style={{ borderColor: "var(--accent-red)", color: "var(--accent-red)" }}
                  type="button"
                >
                  <span className="dot dot--red" /> Unknown
                </button>
                <button className="btn" onClick={() => setFlipped((f) => !f)} type="button">↻ Flip</button>
                <button
                  className="btn"
                  onClick={() => advance(true)}
                  style={{ borderColor: "var(--accent-moss)", color: "var(--accent-moss)" }}
                  type="button"
                >
                  <span className="dot dot--moss" /> Known
                </button>
              </div>
              <div className="t-mono fc-kbd-hint" style={{ fontSize: 11, color: "var(--text-faint)", letterSpacing: "0.08em" }}>
                SPACE flip · ← unknown · → known
              </div>
            </>
          )}

          {finished && (
            <CompletionScreen
              title={<>End of <span className="italic">deck</span>.</>}
              subtitle={`You went through ${deck.length} cards from Day ${pad(day)}.`}
              stats={[
                { label: "Known", value: score.known, dot: "moss" },
                { label: "Unknown", value: score.unknown, dot: "red" },
              ]}
              primaryAction={{ label: "Back to day →", onClick: () => navigate(`/day/${day}`) }}
              secondaryAction={{ label: "↻ Review again", onClick: restart }}
            />
          )}
        </div>
      </section>
    </div>
  );
}

interface CardProps {
  word: Word;
  day: number;
  deckPos: number;
  deckSize: number;
  direction: Direction;
  flipped: boolean;
  onFlip: () => void;
}

function Card({ word, day, deckPos, deckSize, direction, flipped, onFlip }: CardProps) {
  return (
    <div className={`flashcard ${flipped ? "is-flipped" : ""}`} onClick={onFlip}>
      <div className="flashcard__inner">
        <div className="flashcard__face">
          <div className="row-s">
            <div className="flashcard__corner-tl">Card {pad(deckPos + 1)} / {pad(deckSize)}</div>
            <div className="flashcard__corner-tl">Day {pad(day)}</div>
          </div>
          <div className="flashcard__main">
            {direction === "en-vi" ? (
              <>
                <h2 className="flashcard__word">{word.word}</h2>
                <div className="flashcard__ipa">/{word.ipa}/</div>
                <POS value={word.pos} />
              </>
            ) : (
              <h2
                className="flashcard__word"
                style={{ fontStyle: "italic", fontSize: "clamp(28px, 5vw, 56px)" }}
              >
                {word.meaning_vi}
              </h2>
            )}
          </div>
          <div className="row-s">
            <div className="flashcard__corner-tl">Entry {pad3(word.index)}</div>
            <div className="flashcard__corner-br">Tap to flip ↻</div>
          </div>
        </div>

        <div className="flashcard__face flashcard__face--back">
          <div className="row-s">
            <div className="flashcard__corner-tl">Card {pad(deckPos + 1)} / {pad(deckSize)}</div>
            <div className="flashcard__corner-tl">Verso</div>
          </div>
          <div className="flashcard__main">
            {direction === "en-vi" ? (
              <>
                <div className="flashcard__meaning">{word.meaning_vi}</div>
                <hr className="rule" style={{ width: 60, margin: "12px 0" }} />
                <p
                  className="t-display"
                  style={{ fontSize: "var(--t-md)", color: "var(--text-dim)", maxWidth: 380, margin: 0, lineHeight: 1.5 }}
                >
                  “{word.example_en}”
                </p>
                <p
                  className="t-display italic"
                  style={{ fontSize: "var(--t-sm)", color: "var(--text-muted)", maxWidth: 380, margin: 0 }}
                >
                  {word.example_vi}
                </p>
              </>
            ) : (
              <>
                <h2 className="flashcard__word">{word.word}</h2>
                <div className="flashcard__ipa">/{word.ipa}/</div>
                <POS value={word.pos} />
              </>
            )}
          </div>
          <div className="row-s">
            <div className="flashcard__corner-tl">Entry {pad3(word.index)}</div>
            <div className="flashcard__corner-br">Tap to flip ↺</div>
          </div>
        </div>
      </div>
    </div>
  );
}
